<template>
  <div class="customers data--cards">
    <div class="page__header">
      <div class="page__title">
        {{ $t(`pageTitles.${"potential"}`) }} 
      </div>
      <div class="page__right">
        <div class="selected-filters" v-if="selectedTeamIds.length > 0 || Object.keys(searchPotentialCustomerForms).length > 0">
          <div v-for="teamId in selectedTeamIds" :key="`team${teamId}`" class="selected-filters__filter">
            <span>Team: {{ getTeamDisplayName(teamId) }}</span>
            <div class="selected-filters__remove" @click="removeTeam(teamId)"></div>
          </div>

          <div class="selected-filters__filter" v-if="startDateFrom || startDateTo">
            <span>{{ $t(`searchCustomerFields.${"policyStartDate"}`) }}: {{ formatDate(startDateFrom) || '...' }} - {{
              formatDate(startDateTo) || '...' }}</span>
            <div class="selected-filters__remove" @click="clearStartDateRange()"></div>
          </div>

          <div class="selected-filters__filter" v-if="endDateFrom || endDateTo">
            <span>{{ $t(`searchCustomerFields.${"policyEndDate"}`) }}: {{ formatDate(endDateFrom) || '...' }} - {{
              formatDate(endDateTo) || '...' }}</span>
            <div class="selected-filters__remove" @click="clearEndDateRange()"></div>
          </div>

          <div class="selected-filters__filter" v-if="birthdayFrom || birthdayTo">
            <span>{{ $t('searchCustomerFields.birthday') }}: {{ formatDate(birthdayFrom) || '...' }} - {{ formatDate(birthdayTo) || '...' }}</span>
            <div class="selected-filters__remove" @click="clearBirthday()"></div>
          </div>

          <div class="selected-filters__filter" v-if="zipFrom || zipTo">
            <span>{{ $t('searchCustomerFields.zip') }}: {{ formatRange(zipFrom, zipTo) }}</span>
            <div class="selected-filters__remove" @click="clearZip()"></div>
          </div>

          <div class="selected-filters__filter" v-for="agentId in selectedAgents" :key="`agent${agentId}`">
            <span>Agent: 
              {{
                (searchPotentialCustomerForms.agents && searchPotentialCustomerForms.agents.find(a => a.id == agentId))
                  ? searchPotentialCustomerForms.agents.find(a => a.id == agentId).first_name + ' ' +
                  searchPotentialCustomerForms.agents.find(a => a.id == agentId).last_name
                  : 'Agent ' + agentId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeAgent(agentId)"></div>
          </div>


          <div class="selected-filters__filter" v-for="typeId in selectedCompanyTypes" :key="`type${typeId}`">
            <span>Company Type: {{
              (searchPotentialCustomerForms.companyTypes && searchPotentialCustomerForms.companyTypes.find(item => item.id === typeId))
                ? searchPotentialCustomerForms.companyTypes.find(item => item.id === typeId).type
                : 'Type ' + typeId
            }}</span>
            <div class="selected-filters__remove" @click="removeCompanyType(typeId)"></div>
          </div>


          <div class="selected-filters__filter" v-for="categoryId in selectedBranchCategories"
            :key="`category${categoryId}`">
            <span>Branch Category: {{
              (searchPotentialCustomerForms.branchCategories && searchPotentialCustomerForms.branchCategories.find(item => item.id === categoryId))
                ? searchPotentialCustomerForms.branchCategories.find(item => item.id === categoryId)[`name`]
                : 'Category ' + categoryId
            }}</span>
            <div class="selected-filters__remove" @click="removeBranchCategory(categoryId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="priorityId in selectedPriorities" :key="`priority${priorityId}`">
            <span>Priority: {{
              (searchPotentialCustomerForms.priorities && searchPotentialCustomerForms.priorities.find(item => item.id === priorityId))
                ? searchPotentialCustomerForms.priorities.find(item => item.id === priorityId).type
                : 'Priority ' + priorityId
            }}</span>
            <div class="selected-filters__remove" @click="removePriority(priorityId)"></div>
          </div>

        </div>
        <form class="search search--filter" @submit.prevent="updateQuery()">
          <div class="search__wrapper">
            <input class="search__search" ref="search" :class="{ 'search__search--active': searchKeywords }" type="text"
              v-model="searchKeywords" />
            <div class="search__icon"></div>
            <div class="selected-filters__filter search__placeholder" v-if="searchPlaceholder">
              <div class="search__placeholder-content">
                {{ searchPlaceholder }}
                <div class="selected-filters__remove" @click="removeSearch()"></div>
              </div>
            </div>
            <div class="search__splitter"></div>
          </div>
          <div class="search__filter" ref="filterToggler" @click="showSearchFilter = true"></div>
          <SearchFilter v-if="showSearchFilter" :filterToggler="filterToggler" :searchType="'potentialCustomer'"
            :toggleSearchFilter="(state) => {
                showSearchFilter = state;
              }
              " />
        </form>
        <button type="button" class="button button--default search-button button--animated"
          v-if="canAccessPotentialCustomers && canCreatePotentialCustomer"
          @click="openSearchPotentialCustomerModal">
          <div class="button__icon"></div>
        </button>
      </div>
    </div>
    <div class="data_content">  
      <Loading v-if="customersLoading" />
      <div v-else-if="!canAccessPotentialCustomers" style="height: 80vh;">
        <NoData type="no-permission" />
      </div>
      <NoData v-else-if="potentialCustomerCollectionError?.status === 403" type="no-permission" />
      <PotentialPoliciesTable v-else :customers="customersPerPage.data || []" @refresh="searchCustomers" />
    </div>
    <Pagination v-if="!customersLoading && customersPerPage.data && customersPerPage.data.length > 0" :limit="3" :data="customersPerPage" @pagination-change-page="getResults"></Pagination>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import PotentialPoliciesTable from "@/components/PotentialPoliciesTable";
import Loading from "@/partials/Loading";
import SearchFilter from "@/partials/SearchFilter";
import NoData from "@/partials/NoData";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Template refs
const search = ref(null);
const filterToggler = ref(null);

// Reactive data
const searchPlaceholder = ref("");
const searchKeywords = ref("");
const showSearchFilter = ref(false);
const selectedTeamIds = ref([]);
const selectedAgents = ref([]);
const selectedBranchCategories = ref([]);
const selectedCompanyTypes = ref([]);
const selectedPriorities = ref([]);
const startDateFrom = ref("");
const startDateTo = ref("");
const endDateFrom = ref("");
const endDateTo = ref("");
const birthdayFrom = ref("");
const birthdayTo = ref("");
const zipFrom = ref("");
const zipTo = ref("");

// Computed properties
const customersPerPage = computed(() => store.getters.customersPerPage);
const customersLoading = computed(() => store.getters.potentialCustomersLoading);
const searchPotentialCustomerForms = computed(() => store.getters.searchPotentialCustomerForms);
 
const potentialCustomerCollectionError = computed(() => store.getters.potentialCustomerCollectionError);
 
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);

const canAccessPotentialCustomers = computed(() => {
  if (isSuperAdmin.value) return true;

  const authorizedTeams = store.getters.authorizedTeamIds('customer_access');
  return authorizedTeams === null || (Array.isArray(authorizedTeams) && authorizedTeams.length > 0);
});

const canCreatePotentialCustomer = computed(() => {
  if (isSuperAdmin.value) return true;

  const authorizedTeams = store.getters.authorizedTeamIds('customer_create');
  return authorizedTeams === null || (Array.isArray(authorizedTeams) && authorizedTeams.length > 0);
});

const openSearchPotentialCustomerModal = () => {
  if (!canAccessPotentialCustomers.value || !canCreatePotentialCustomer.value) {
    return;
  }

  store.commit("setShowSearchPotentialCustomerModal", true);
};

// Methods
const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const formatRange = (from, to) => {
  if (!from && !to) return '';
  if (from && !to) return `${from} - ...`;
  if (!from && to) return `... - ${to}`;
  return `${from} - ${to}`;
};

const getTeamDisplayName = (teamId) => {
  const resolvedTeamId = Number(teamId);
  if (!Number.isInteger(resolvedTeamId) || resolvedTeamId <= 0) {
    return 'All authorized teams';
  }

  const teamNameFromMembership = store.getters.membershipFor(resolvedTeamId)?.team?.name;
  if (teamNameFromMembership) return teamNameFromMembership;

  const teamNameFromAuthorizationMembership = Array.isArray(store.getters.authorizationMemberships)
    ? store.getters.authorizationMemberships.find((membership) => Number(membership?.team?.id) === resolvedTeamId)?.team?.name
    : null;
  if (teamNameFromAuthorizationMembership) return teamNameFromAuthorizationMembership;

  const teamNameFromList = Array.isArray(store.getters.teams)
    ? store.getters.teams.find((team) => Number(team?.id) === resolvedTeamId)?.name
    : null;
  if (teamNameFromList) return teamNameFromList;

  const userTeamName = Array.isArray(store.getters.loggedUser?.teams)
    ? store.getters.loggedUser.teams.find((team) => Number(team?.id) === resolvedTeamId)?.name
    : null;
  if (userTeamName) return userTeamName;

  const currentTeamId = Number(store.getters.currentTeamId);
  if (Number.isInteger(currentTeamId) && currentTeamId > 0 && currentTeamId === resolvedTeamId) {
    return store.getters.membershipFor(currentTeamId)?.team?.name || `Team ${resolvedTeamId}`;
  }

  return `Team ${resolvedTeamId}`;
};

const loadFiltersFromQuery = (q) => {
  searchKeywords.value = q.search || "";
  const normalizedTeamIds = Array.isArray(q.teams)
    ? q.teams
    : typeof q.teams === 'string' && q.teams.length
      ? q.teams.split(',')
      : [];

  selectedTeamIds.value = normalizedTeamIds
    .flatMap((teamId) => String(teamId).split(','))
    .map((teamId) => Number(teamId))
    .filter((teamId) => Number.isInteger(teamId) && teamId > 0);
  selectedAgents.value = q.agents ? q.agents.split(",").map(Number) : [];
  selectedBranchCategories.value = q.branchCategories ? q.branchCategories.split(",").map(Number) : [];
  selectedCompanyTypes.value = q.companyTypes ? q.companyTypes.split(",").map(Number) : [];
  selectedPriorities.value = q.priorities ? q.priorities.split(",").map(Number) : [];
  startDateFrom.value = q.startDateFrom || "";
  startDateTo.value = q.startDateTo || "";
  endDateFrom.value = q.endDateFrom || "";
  endDateTo.value = q.endDateTo || "";
  birthdayFrom.value = q.birthday_from || q.birthdayFrom || q.birthday || "";
  birthdayTo.value = q.birthday_to || q.birthdayTo || q.birthday || "";
  zipFrom.value = q.zip_from || "";
  zipTo.value = q.zip_to || "";
};

const searchCustomers = () => {
  const page = Number(route.query.page) || 1;

  const payload = {
    search: searchKeywords.value,
    page: page,
    teamId: selectedTeamIds.value.length > 0 ? selectedTeamIds.value : null,
    agents: selectedAgents.value,
    branchCategories: selectedBranchCategories.value,
    companyTypes: selectedCompanyTypes.value,
    priorities: selectedPriorities.value,
    startDateFrom: startDateFrom.value,
    startDateTo: startDateTo.value,
    endDateFrom: endDateFrom.value,
    endDateTo: endDateTo.value,
    zip_from: zipFrom.value,
    zip_to: zipTo.value,
    birthday_from: birthdayFrom.value,
    birthday_to: birthdayTo.value
  };

  store.dispatch("searchPotentialCustomers", payload)
    .then(() => {
      if (customersPerPage.value.data && customersPerPage.value.data.length === 0 && page > 1) {
        getResults(page - 1);
      }
    })
    .catch(error => {
      console.error("Search failed:", error);
    });
};

const updateQuery = () => {
  router.replace({
    query: {
      page: 1,
      search: searchKeywords.value || undefined,
      teams: selectedTeamIds.value.length > 0 ? selectedTeamIds.value : undefined,
      agents: selectedAgents.value.length ? selectedAgents.value.join(",") : undefined,
      branchCategories: selectedBranchCategories.value.length ? selectedBranchCategories.value.join(",") : undefined,
      companyTypes: selectedCompanyTypes.value.length ? selectedCompanyTypes.value.join(",") : undefined,
      priorities: selectedPriorities.value.length ? selectedPriorities.value.join(",") : undefined,
      startDateFrom: startDateFrom.value || undefined,
      startDateTo: startDateTo.value || undefined,
      endDateFrom: endDateFrom.value || undefined,
      endDateTo: endDateTo.value || undefined,
      birthday_from: birthdayFrom.value || undefined,
      birthday_to: birthdayTo.value || undefined,
      zip_from: zipFrom.value || undefined,
      zip_to: zipTo.value || undefined,
    },
  }).catch(() => { });
};

const clearAllDependentFilters = () => {
  selectedAgents.value = [];
  selectedBranchCategories.value = [];
  selectedCompanyTypes.value = [];
  selectedPriorities.value = [];
  birthdayFrom.value = "";
  birthdayTo.value = "";
  zipFrom.value = "";
  zipTo.value = "";
  startDateFrom.value = "";
  startDateTo.value = "";
  endDateFrom.value = "";
  endDateTo.value = "";

  store.commit("setSearchPotentialCustomerForms", {});
};

const clearStartDateRange = () => {
  startDateFrom.value = "";
  startDateTo.value = "";
  updateQuery();
};

const clearEndDateRange = () => {
  endDateFrom.value = "";
  endDateTo.value = "";
  updateQuery();
};

const clearBirthday = () => {
  birthdayFrom.value = "";
  birthdayTo.value = "";
  updateQuery();
};

const clearZip = () => {
  zipFrom.value = "";
  zipTo.value = "";
  updateQuery();
};

const removeTeam = (teamId) => {
  selectedTeamIds.value = selectedTeamIds.value.filter(id => id !== teamId);
  if (selectedTeamIds.value.length === 0) {
    clearAllDependentFilters();
  }
  updateQuery();
};

const removeAgent = (id) => {
  selectedAgents.value = selectedAgents.value.filter((x) => x !== id);
  updateQuery();
};

const removeCompanyType = (id) => {
  selectedCompanyTypes.value = selectedCompanyTypes.value.filter((x) => x !== id);
  updateQuery();
};

const removeBranchCategory = (id) => {
  selectedBranchCategories.value = selectedBranchCategories.value.filter((x) => x !== id);
  updateQuery();
};

const removePriority = (id) => {
  selectedPriorities.value = selectedPriorities.value.filter((x) => x !== id);
  updateQuery();
};

const removeSearch = () => {
  searchKeywords.value = "";
  updateQuery();
};

const getResults = (page) => {
  if (Number(route.query.page) === Number(page)) return;

  router.replace({
    query: { ...route.query, page: page },
  });
};

// Watchers
watch(
  () => route.query,
  (query) => {
    if (!query.page) {
      router.replace({ query: { ...query, page: 1 } });
      return;
    }
    loadFiltersFromQuery(query);
    searchCustomers();
  },
  { deep: true, immediate: true }
);

// Lifecycle
onMounted(async () => {
  // Page-level access control
  if (!canAccessPotentialCustomers.value) {
    router.replace("/dashboard");
    return;
  }

  if (!Array.isArray(store.getters.teams) || store.getters.teams.length === 0) {
    await store.dispatch("getTeams");
  }
});

</script>

<style scoped lang="scss">
.search-button {
  transform: translateX(0) !important;
  margin-right: 0px !important;
}

.customers {
  height: 100%;

  &__content {
    margin-bottom: 20px;
    height: calc(100% - 48px - 15px - 16px - 20px);
  }
}

@media (max-width: 640px) {
  .page__right .button {
    transform: initial !important;
    margin-right: 0 !important;
    flex: 0 0 100% !important;
  }
}
</style>
